import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/contexts/AuthContext'
import { Loader2 } from 'lucide-react'

/**
 * Welcome Page - First step of onboarding (Step 1)
 * User signs up with Google or email
 * Flow: Welcome → Setup → Personalisation
 */
export function WelcomePage() {
    const navigate = useNavigate()
    const { user, signUpWithEmail, signInWithGoogle, loading: authLoading } = useAuth()
    const [searchParams] = useSearchParams()
    const [fullName, setFullName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Get redirect parameter from URL (in case someone reaches signup with a redirect)
    const redirectPath = searchParams.get('redirect')

    // If user is already logged in, redirect appropriately
    useEffect(() => {
        if (user && !authLoading) {
            // If there's a redirect path, use it; otherwise go to setup
            const targetPath = redirectPath || '/app/onboarding/setup'
            navigate(targetPath)
        }
    }, [user, authLoading, redirectPath, navigate])

    const handleGoogleSignup = async () => {
        setError(null)
        setLoading(true)
        try {
            await signInWithGoogle()
            // OAuth will redirect, so we don't need to navigate
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sign up with Google')
            setLoading(false)
        }
    }

    const handleEmailSignup = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)

        if (!fullName.trim()) {
            setError('Please enter your full name')
            return
        }
        if (!email.trim()) {
            setError('Please enter your email')
            return
        }
        if (!password.trim() || password.length < 6) {
            setError('Password must be at least 6 characters')
            return
        }

        setLoading(true)
        try {
            await signUpWithEmail(email, password, fullName)
            // Navigation will happen via the useEffect when user state updates
            // But also try immediate navigation in case session is already set
            navigate('/app/onboarding/setup')
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sign up')
            setLoading(false)
        }
        // Don't set loading to false on success - let the redirect happen
    }

    return (
        <div className="h-screen overflow-hidden flex flex-col md:flex-row bg-white dark:bg-background">
            {/* Left side - Form */}
            <div className="w-full md:w-1/2 h-full flex flex-col justify-between p-8 md:p-16 lg:p-24 relative z-10">
                {/* Main Content */}
                <div className="max-w-md w-full mx-auto animate-fade-in">
                    {/* Logo (Inside main container for alignment) */}
                    <div className="flex items-center gap-2 mb-8 group cursor-pointer hover:opacity-90 transition-opacity">
                        <img 
                            src="/ReflowAI Logo.png" 
                            alt="ReflowAI" 
                            className="h-10 w-10 rounded-lg object-contain group-hover:scale-105 transition-transform"
                            width="50"
                            height="50"
                        />
                        <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">ReflowAI</span>
                    </div>

                    <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white leading-tight animate-slide-up stagger-1">
                        Welcome to Reflow!
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-10 leading-relaxed animate-slide-up stagger-2">
                        Sign up to track real-time competitor pricing and outsmart the quick commerce market in minutes.
                    </p>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm animate-fade-in">
                            {error}
                        </div>
                    )}

                    {/* Google Sign Up Button */}
                    <div className="animate-slide-up stagger-3">
                        <Button
                            onClick={handleGoogleSignup}
                            variant="outline"
                            disabled={loading}
                            className="w-full flex items-center justify-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-white hover:text-gray-700 dark:hover:text-white font-medium py-6 px-4 rounded-xl transition-all shadow-sm mb-8 h-auto group active:scale-[0.98] disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <img alt="Google Logo" className="w-5 h-5 group-hover:scale-110 transition-transform" src="/onboarding/google-logo.svg" />
                            )}
                            <span>Sign up with Google</span>
                        </Button>
                    </div>

                    {/* Divider */}
                    <div className="relative flex py-2 items-center mb-8 animate-fade-in stagger-4">
                        <div className="flex-grow border-t border-gray-100 dark:border-gray-800" />
                        <span className="flex-shrink-0 mx-4 text-gray-400 text-xs font-semibold tracking-widest">OR</span>
                        <div className="flex-grow border-t border-gray-100 dark:border-gray-800" />
                    </div>

                    {/* Email Form */}
                    <form className="space-y-4" onSubmit={handleEmailSignup}>
                        <div className="animate-slide-up stagger-5 space-y-4">
                            <div>
                                <label className="sr-only" htmlFor="full-name">Full Name</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary transition-colors">
                                        <span className="material-icons text-[20px]">person_outline</span>
                                    </div>
                                    <Input
                                        id="full-name"
                                        type="text"
                                        placeholder="Full name"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        disabled={loading}
                                        className="block w-full pl-11 pr-4 py-4 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm transition-all h-auto hover:border-gray-300 dark:hover:border-gray-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="sr-only" htmlFor="email">Email address</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary transition-colors">
                                        <span className="material-icons text-[20px]">mail_outline</span>
                                    </div>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={loading}
                                        className="block w-full pl-11 pr-4 py-4 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm transition-all h-auto hover:border-gray-300 dark:hover:border-gray-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="sr-only" htmlFor="password">Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary transition-colors">
                                        <span className="material-icons text-[20px]">lock_outline</span>
                                    </div>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Password (min 6 characters)"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        className="block w-full pl-11 pr-4 py-4 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm transition-all h-auto hover:border-gray-300 dark:hover:border-gray-600"
                                    />
                                </div>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all hover:shadow-xl hover:shadow-blue-500/40 active:scale-[0.99] mt-2 h-auto disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                'Continue'
                            )}
                        </Button>
                    </form>

                    <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400 animate-fade-in stagger-6">
                        Already have an account? <Link className="text-primary hover:underline font-semibold" to={redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : "/login"}>Log in</Link>
                    </p>
                </div>

                {/* Footer */}
                <div className="mt-auto pt-6 mx-auto w-full max-w-md text-center">
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        By signing up for a Reflow account, you agree to our <a className="underline hover:text-primary transition-colors" href="#">Privacy Policy</a> and <a className="underline hover:text-primary transition-colors" href="#">Terms of Service</a>.
                    </p>
                </div>
            </div>

            {/* Right side - Decorative Panel */}
            <div className="hidden md:flex md:w-1/2 bg-[#0F172A] relative overflow-hidden items-center justify-center p-12">
                {/* Background Gradients */}
                <div className="absolute top-[-10%] right-[-10%] w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] pointer-events-none" />

                {/* Abstract Image Background - Fixed to fill height and width */}
                <div className="absolute inset-0 opacity-60 pointer-events-none">
                    <div className="w-full h-full bg-gradient-to-b from-transparent via-[#0F172A]/20 to-[#0F172A] absolute inset-0 z-10" />
                    <img
                        alt="Abstract 3D Blue Shape"
                        className="w-full h-full object-cover opacity-50 mix-blend-overlay"
                        src="/onboarding/abstract-blue.jpg"
                    />
                </div>

                {/* Feature Cards */}
                <div className="relative w-full max-w-lg z-10 flex flex-col gap-6">
                    {/* Price Drop Alert Card */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl shadow-2xl animate-float-slow self-end w-72 transform translate-x-4">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-300 border border-purple-500/30">
                                <span className="material-icons text-sm">bolt</span>
                            </div>
                            <div>
                                <p className="text-white font-medium text-sm">Price Drop Alert</p>
                                <p className="text-gray-300 text-xs mt-1">Zepto undercuts Maggi by 5% in Mumbai region.</p>
                            </div>
                        </div>
                        <div className="mt-3 h-1 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full w-2/3 bg-purple-500 rounded-full" />
                        </div>
                    </div>

                    {/* Stockout Warning Card */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl shadow-2xl animate-float-medium self-start w-80 -ml-8">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 border border-green-500/30">
                                <span className="material-icons text-xs">inventory_2</span>
                            </div>
                            <span className="text-white font-medium text-sm">Stockout Warning</span>
                        </div>
                        <div className="flex justify-between items-center bg-black/20 rounded-lg p-2">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-500" />
                                <span className="text-gray-200 text-xs">Blinkit: Out of Stock</span>
                            </div>
                            <span className="text-xs text-gray-400">10m ago</span>
                        </div>
                        <div className="flex justify-between items-center bg-black/20 rounded-lg p-2 mt-2">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500" />
                                <span className="text-gray-200 text-xs">Swiggy: Available</span>
                            </div>
                            <span className="text-xs text-green-400">₹120</span>
                        </div>
                    </div>

                    {/* Market Opportunity Card */}
                    <div className="bg-white/90 text-gray-900 p-5 rounded-2xl shadow-2xl animate-float-fast self-center w-80 transform translate-y-4">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <p className="font-bold text-sm">Market Opportunity</p>
                                <p className="text-gray-500 text-xs">Dairy Category</p>
                            </div>
                            <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-1 rounded-full">+12.5%</span>
                        </div>
                        <div className="flex items-end gap-1 h-16 w-full mt-2">
                            <div className="w-1/5 bg-gray-100 rounded-t-sm h-[40%]" />
                            <div className="w-1/5 bg-gray-200 rounded-t-sm h-[60%]" />
                            <div className="w-1/5 bg-gray-300 rounded-t-sm h-[30%]" />
                            <div className="w-1/5 bg-blue-500 rounded-t-sm h-[85%] relative group">
                                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                    High Demand
                                </div>
                            </div>
                            <div className="w-1/5 bg-gray-200 rounded-t-sm h-[50%]" />
                        </div>
                        <p className="text-xs text-gray-500 mt-3 border-t border-gray-100 pt-2">
                            Detected 3 new competitor SKUs
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
